import { Permission } from '@act/shared/authorization';
import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { Button, FlexContainer } from '@act/storybook';
import { generateDatadogTrackingProps } from '@act/toolkit/environment/environment/userMonitoring.util';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DialogHeader } from 'react-components/dialog/DialogHeader';
import { useForm } from 'react-hook-form';
import { surveysRequests } from 'redux/requests/surveys.requests';
import {
  membersAppSelectors,
  selectSelectedMember,
} from 'redux/slices/memberSlice.slice';
import { surveyActions, surveysSelectors } from 'redux/slices/surveys.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { makeRequest } from 'redux/utils/apiRequest';
import { AdditionalFeedCardActions } from 'toolkit/info-card/InfoCard';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { isEditable } from 'utilities/isEditable.util';
import { TrackingProvider } from '../tracking';
import {
  AssignedSurveyContent,
  NotesForm,
  notesSchema,
} from './components/AssignedSurveyContent';
import useConfirmationAlertDialog from './hooks/useConfirmationAlertDialog.hook';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { handleNoteFieldClose } from './noteFieldClose.util';
import { useAssignedSurveySaveActions } from './hooks/useAssignedSurveySaveActions.hook';
import { NoteFieldRef } from './NoteField';

export interface AssignedSurveyDialogUniqueProps {
  assignedSurveyId: number;
  isReviewMode?: boolean;
}

export type AssignedSurveyDialogProps = GenericDialogProps<boolean> &
  AssignedSurveyDialogUniqueProps;

export type AssignedSurveyDialogCallbackInput = {};

const AssignedSurveyDialog = ({
  assignedSurveyId,
  isReviewMode = false,
  open,
  setOpen,
}: AssignedSurveyDialogProps) => {
  const dispatch = useAppDispatch();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const member: UserDto = useAppSelector(selectSelectedMember);
  const [additionalMenuAnchor, setAdditionalMenuAnchor] =
    useState<null | HTMLElement>(null);
  const menuOpen = Boolean(additionalMenuAnchor);
  const openAdditionalMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdditionalMenuAnchor(event.currentTarget);
  };
  const closeAdditionalMenu = () => {
    setAdditionalMenuAnchor(null);
  };
  function executeHandler(handler: () => void) {
    setAdditionalMenuAnchor(null);
    handler();
  }

  const assignedSurvey: SurveyV2.AssignedSurveyDto =
    surveysSelectors.useSelectAssignedSurveyById(assignedSurveyId);

  const { control, getValues, formState } = useForm<NotesForm>({
    defaultValues: {
      notes: assignedSurvey?.notes || '',
    },
    resolver: zodResolver(notesSchema),
  });

  const [isSurveyFormDirty, setIsSurveyFormDirty] = useState(false);
  const noteFieldRef = useRef<NoteFieldRef>(null);

  const { assignedSurveyNoteSaveOverride, saveAndClose, submittingForm, isSaveDisabled } =
    useAssignedSurveySaveActions({
      assignedSurveyId,
      assignedSurvey,
      isReviewMode,
      noteFieldRef,
      getNotes: () => getValues('notes'),
      notesBaseline: assignedSurvey?.notes || '',
      onClose: () => setOpen(false),
    });

  const handleFormStateChange = useCallback((isDirty: boolean) => {
    setIsSurveyFormDirty(isDirty);
  }, []);

  async function handleSubmit() {
    if (isSurveyFormDirty) {
      void handleUnsavedChanges(() => {
        void saveAndClose();
      });
      return;
    }
    await saveAndClose();
  }

  function handleClose() {
    handleNoteFieldClose({
      mode: 'edit',
      isDirty: isSurveyFormDirty || formState.isDirty,
      noteFieldRef,
      handleUnsavedChanges,
      finishClose: () => setOpen(false),
    });
  }

  const canReopenSurvey = membersAppSelectors.useHasPermissionOnMember(
    assignedSurvey?.memberId,
    Permission.CAN_REOPEN_SURVEYS
  );

  const menuItems = useMemo(() => {
    const items: AdditionalFeedCardActions[] = [];

    if (
      canReopenSurvey &&
      assignedSurvey?.completedAt &&
      isEditable(assignedSurvey.completedAt)
    ) {
      items.push({
        title: 'Reopen Survey',
        handler: async () => {
          const shouldWeReopenSurvey = await openConfirmationAlertDialog({
            title: 'Confirm reopening',
            icon: <ErrorOutlineIcon />,
            iconColor: 'error',
            iconBorderColor: 'errorContainer',
            avatarCaption: 'Are you sure?',
            message: 'Are you sure you want to reopen this survey?',
            confirmationText: 'Yes, reopen it',
            confirmButtonColor: 'error',
          });
          if (shouldWeReopenSurvey) {
            const updatedAssignedSurvey = await makeRequest(
              surveysRequests.reopenAssignedSurvey,
              {
                params: { assignedSurveyId: assignedSurvey.id },
                body: null,
              }
            );

            dispatch(surveyActions.upsertAssignedSurvey(updatedAssignedSurvey));
            handleClose();
          }
        },
      });
    }
    return items;
  }, [canReopenSurvey, assignedSurvey]);

  return (
    <TrackingProvider contextName="assigned-survey-dialog">
      <Dialog
        maxWidth="md"
        open={open}
        onClose={handleClose}
        scroll="body"
        aria-labelledby="assinged-survey-dialog"
        aria-describedby="assigned-survey-dialog"
        role="dialog"
      >
        <DialogHeader
          postfixAction={{
            title: 'close',
            callback: handleClose,
            icon: <CloseIcon />,
          }}
        >
          <FlexContainer direction="row" alignItems="center" width="100%">
            {`${isReviewMode ? 'Reviewing s' : 'S'}urvey for ${
              member.firstName
            } ${member.lastName.slice(0, 1)}`}
            {menuItems?.length > 0 && (
              <FlexContainer
                direction="row"
                flexGrow={1}
                justifyContent="flex-end"
              >
                <IconButton
                  aria-label="settings"
                  onClick={openAdditionalMenu}
                  color="default"
                  {...generateDatadogTrackingProps('assigned-survey-settings')}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={additionalMenuAnchor}
                  open={menuOpen}
                  onClose={closeAdditionalMenu}
                >
                  {menuItems.map((item, index) => (
                    <MenuItem
                      onClick={() => {
                        executeHandler(item.handler);
                      }}
                      key={`menu-item-${index}`}
                      {...generateDatadogTrackingProps(
                        `assigned-survey-settings-select-${item.title}`
                      )}
                    >
                      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                      <ListItemText>{item.title}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </FlexContainer>
            )}
          </FlexContainer>
        </DialogHeader>
        <DialogContent>
          <FlexContainer
            direction="column"
            width="525px"
            padding="20px 12px 12px 24px"
          >
            <DialogContentText component="div">
            <AssignedSurveyContent
              assignedSurvey={assignedSurvey}
              member={member}
              isReviewMode={isReviewMode}
              submittingForm={submittingForm}
              onFormStateChange={handleFormStateChange}
              control={control}
              formState={formState}
              noteFieldRef={noteFieldRef}
              noteSave={assignedSurveyNoteSaveOverride}
            />
            </DialogContentText>
          </FlexContainer>
        </DialogContent>
        <DialogActions>
          <FlexContainer
            direction="row"
            justifyContent="flex-end"
            width="100%"
            padding="8px 32px 8px 24px"
            margin="0 0 8px"
          >
            <Button
              variant="contained"
              type="submit"
              debounced
              disabled={isSaveDisabled || submittingForm}
              title={
                isSaveDisabled
                  ? 'Notes could not be saved. Resolve the error above and try again.'
                  : undefined
              }
              onClick={handleSubmit}
              {...generateDatadogTrackingProps('save-and-close')}
            >
              Save and close
            </Button>
          </FlexContainer>
        </DialogActions>
      </Dialog>
    </TrackingProvider>
  );
};

export default AssignedSurveyDialog;
